import React, { useState, useEffect, useCallback } from 'react';
import {
    Send, Eye, X, Check, Clock, AlertTriangle, FileText, Copy, ChevronLeft,
    Tablet as TabletIcona, PencilLine,
} from 'lucide-react';
import { questionariAPI } from '../../services/questionariApi';
import CompilaQuestionario from './CompilaQuestionario';
import '../../styles/Questionari.css';
import '../../styles/QuestionariConsole.css';

/**
 * ElencoQuestionari — invio al tablet, compilazione alla postazione e
 * lettura di quanto compilato.
 *
 * Serve sia i questionari psicologici sia le schede cliniche: cambia solo
 * l'`area`. Una seconda copia per la scheda gastroenterologica avrebbe
 * fatto divergere due schermate che devono comportarsi allo stesso modo -
 * gli stessi stati, lo stesso storico, la stessa lettura in cartella.
 *
 * COSA NON C'E', E PERCHE'
 * Nessun punteggio, nessuna soglia, nessun semaforo. I cinque strumenti
 * caricati dichiarano, nel file stesso da cui provengono, che le regole
 * di attribuzione del punteggio non erano presenti nelle scansioni.
 * Sommare i codici numerici delle risposte darebbe un numero, e quel
 * numero verrebbe letto come un risultato clinico da chi apre la
 * cartella. Il gestionale mostra le risposte, che sono un fatto; il
 * punteggio arrivera' quando arriveranno le regole.
 *
 * LE DOMANDE SENZA RISPOSTA SI VEDONO
 * Un questionario consegnato con dei buchi resta consultabile, e dice
 * quali item mancano. Nasconderli farebbe sembrare completo qualcosa che
 * non lo e'.
 */

const dataOra = (v) => {
    if (!v) return '—';
    const d = new Date(v);
    return isNaN(d) ? '—' : d.toLocaleString('it-IT', {
        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
};

const STATO = {
    in_attesa: { testo: 'in attesa sul tablet', classe: 'attesa', Icona: Clock },
    compilato: { testo: 'compilato', classe: 'fatto', Icona: Check },
    annullato: { testo: 'annullato', classe: 'nulla', Icona: X },
    /* Va tenuto allineato agli stati che il database ammette: piu' sotto
       `STATO[c.stato]` viene usato senza guardia, quindi uno stato non
       previsto qui non fa una riga sbagliata, fa saltare la schermata. */
    scaduto: { testo: 'scaduto senza risposte', classe: 'nulla', Icona: Clock },
};

/* ------------------------------------------------------------------ */

const Dettaglio = ({ id, onChiudi }) => {
    const [d, setD] = useState(null);
    const [caricamento, setCaricamento] = useState(true);
    const [errore, setErrore] = useState(null);
    const [copiato, setCopiato] = useState(false);

    useEffect(() => {
        let vivo = true;
        questionariAPI.dettaglio(id)
            .then((r) => { if (vivo) setD(r.data.data); })
            .catch((e) => { if (vivo) setErrore(e?.response?.data?.message || 'Non riesco a leggere la compilazione'); })
            .finally(() => { if (vivo) setCaricamento(false); });
        return () => { vivo = false; };
    }, [id]);

    if (caricamento) return <p className="qz-attesa">Caricamento…</p>;
    if (errore) return <p className="qz-errore"><AlertTriangle size={15} /> {errore}</p>;

    const { compilazione: c, completezza, righe, istruzioni, testo, somma } = d;

    return (
        <div className="qz-dettaglio">
            <div className="qz-dett-testa">
                <button className="qz-btn" onClick={onChiudi}>
                    <ChevronLeft size={15} /> Torna all&apos;elenco
                </button>
                {testo && (
                    <button className="qz-btn"
                            onClick={() => {
                                navigator.clipboard?.writeText(testo);
                                setCopiato(true);
                                setTimeout(() => setCopiato(false), 2500);
                            }}>
                        <Copy size={14} /> {copiato ? 'Copiato' : 'Copia il testo'}
                    </button>
                )}
            </div>

            <h3 className="qz-dett-titolo">
                {c.sigla ? `${c.sigla} — ` : ''}{c.nome}
            </h3>
            <p className="qz-dett-meta">
                {c.stato === 'compilato'
                    ? <>Compilato il {dataOra(c.compilata_il)}
                        {c.modalita === 'postazione'
                            ? <> alla postazione{c.compilata_da_username && `, da ${c.compilata_da_username}`}</>
                            : c.dispositivo && <> su {c.dispositivo}</>}</>
                    : <>Inviato il {dataOra(c.avviata_il)} — {STATO[c.stato]?.testo}</>}
                {' · '}
                {completezza.risposti} risposte su {completezza.totale}
            </p>

            {completezza.mancanti.length > 0 && (
                <p className="qz-avviso">
                    <AlertTriangle size={15} />
                    {completezza.mancanti.length === 1
                        ? `La domanda ${completezza.mancanti[0]} è rimasta senza risposta.`
                        : `${completezza.mancanti.length} domande sono rimaste senza risposta: `
                          + completezza.mancanti.join(', ') + '.'}
                </p>
            )}

            {/* La somma sta PRIMA delle risposte perche' e' la prima cosa
                che si cerca, e con la spiegazione attaccata perche' e' la
                prima cosa che si fraintende. */}
            {somma && c.stato === 'compilato' && (
                <div className="qz-somma">
                    <div className="qz-somma-numero">
                        <b>{somma.somma}</b>
                        {somma.massimo_sui_risposti !== null && (
                            <span>su {somma.massimo_sui_risposti} massimi</span>
                        )}
                    </div>
                    <div className="qz-somma-spiega">
                        <strong>Somma dei codici di risposta</strong>
                        <small>
                            Calcolata su {somma.risposti} item di {somma.su}.
                            <b> Non è un punteggio clinico</b>: le regole di attribuzione
                            del punteggio non erano nel documento di origine dello
                            strumento, quindi non si sa quali item vadano invertiti o
                            pesati. Serve a confrontare lo stesso questionario sulla
                            stessa persona in due momenti — non fra persone diverse, e
                            non contro una soglia, perché soglia non ce n&apos;è.
                        </small>
                    </div>
                </div>
            )}

            {istruzioni && (
                <p className="qz-consegna">
                    <FileText size={14} /> <span>{istruzioni}</span>
                </p>
            )}

            <table className="qz-risposte">
                <tbody>
                    {righe.map((r) => (
                        <tr key={r.n} className={r.risposto ? '' : 'vuota'}>
                            <td className="qz-num">{r.n}</td>
                            <td className="qz-testo">
                                {r.testo}
                                {/* Il riferimento sta sotto il nome del campo e
                                    non accanto al valore: accanto al valore
                                    sembrerebbe un giudizio sul valore. */}
                                {r.nota && <span className="qz-rif">{r.nota}</span>}
                            </td>
                            <td className="qz-valore">
                                {r.risposto
                                    ? <>{r.etichetta || r.valore}
                                        {r.etichetta && <span className="qz-codice">{r.valore}</span>}</>
                                    : <span className="qz-nonrisposto">non risposto</span>}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            <p className="qz-nota">
                Nessun punteggio calcolato: le regole di attribuzione del punteggio non
                erano presenti nel documento di origine dello strumento. I numeri
                accanto alle risposte sono codici, non punti.
            </p>
        </div>
    );
};

/* ------------------------------------------------------------------ */

const ElencoQuestionari = ({
    pazienteId, area = 'psicologico', etichetta = 'questionario',
    /* Arrivando da un avviso che indica una compilazione precisa, si apre
       quella invece dell'elenco. Si consuma una volta sola: chiudendola si
       resta sull'elenco, come se ci si fosse arrivati a mano. */
    apriCompilazione = null,
}) => {
    const [questionari, setQuestionari] = useState([]);
    const [compilazioni, setCompilazioni] = useState([]);
    const [dispositivi, setDispositivi] = useState([]);
    const [caricamento, setCaricamento] = useState(true);
    const [errore, setErrore] = useState(null);
    const [messaggio, setMessaggio] = useState(null);
    const [inCorso, setInCorso] = useState(false);
    const [apri, setApri] = useState(apriCompilazione || null);   // id compilazione aperta
    const [scelta, setScelta] = useState(null);      // questionario da inviare
    const [dispositivo, setDispositivo] = useState('');
    const [allaPostazione, setAllaPostazione] = useState(null);   // { questionario, compilazioneId }

    const carica = useCallback(async () => {
        try {
            const [q, c, d] = await Promise.all([
                questionariAPI.elenco(area),
                questionariAPI.perPaziente(pazienteId),
                questionariAPI.dispositivi().catch(() => ({ data: { data: { dispositivi: [] } } })),
            ]);
            setQuestionari(q.data.data.questionari || []);
            setCompilazioni(c.data.data.compilazioni || []);
            const disp = d.data.data.dispositivi || [];
            setDispositivi(disp);
            if (disp.length === 1) setDispositivo(String(disp[0].id));
            setErrore(null);
        } catch (e) {
            setErrore(e?.response?.data?.message || 'Non riesco a leggere i questionari');
        } finally {
            setCaricamento(false);
        }
    }, [pazienteId, area]);

    useEffect(() => { carica(); }, [carica]);

    const invia = async () => {
        if (!scelta || !dispositivo) return;
        try {
            setInCorso(true);
            setErrore(null);
            const r = await questionariAPI.invia(pazienteId, scelta.id, Number(dispositivo));
            setMessaggio(r.data.message);
            setScelta(null);
            await carica();
        } catch (e) {
            setErrore(e?.response?.data?.message || 'Invio non riuscito');
        } finally {
            setInCorso(false);
        }
    };

    /* Compilazione qui, senza tablet: per chi non lo usa. Se ce n'e' gia'
       una in corso - iniziata al tablet e non finita, o interrotta qui -
       si riprende quella con le risposte che ha. */
    const compilaQui = async (q) => {
        try {
            setInCorso(true);
            setErrore(null);
            setMessaggio(null);
            const r = await questionariAPI.apri(pazienteId, q.id);
            setScelta(null);
            setAllaPostazione({
                questionario: r.data.data.questionario,
                compilazioneId: r.data.data.compilazione_id,
                anagrafica: r.data.data.anagrafica || {},
            });
            if (r.data.data.ripresa) setMessaggio('Ripresa la compilazione già iniziata.');
        } catch (e) {
            setErrore(e?.response?.data?.message || 'Impossibile aprire il questionario');
        } finally {
            setInCorso(false);
        }
    };

    const annulla = async (id) => {
        try {
            setInCorso(true);
            const r = await questionariAPI.annulla(id, 'Annullato dalla postazione');
            setMessaggio(r.data.message);
            await carica();
        } catch (e) {
            setErrore(e?.response?.data?.message || 'Impossibile annullare');
        } finally {
            setInCorso(false);
        }
    };

    if (caricamento) return <p className="qz-attesa">Caricamento…</p>;
    if (apri) return <Dettaglio id={apri} onChiudi={() => setApri(null)} />;

    if (allaPostazione) {
        return (
            <CompilaQuestionario
                pazienteId={pazienteId}
                questionario={allaPostazione.questionario}
                compilazioneId={allaPostazione.compilazioneId}
                anagrafica={allaPostazione.anagrafica}
                onChiudi={() => { setAllaPostazione(null); carica(); }}
                onFatto={(m) => { setAllaPostazione(null); setMessaggio(m); carica(); }}
            />
        );
    }

    // L'ultima compilazione per ciascuno strumento, che e' quella che conta
    const ultima = new Map();
    for (const c of compilazioni) {
        if (!ultima.has(c.questionario_id)) ultima.set(c.questionario_id, c);
    }
    const storico = compilazioni.filter((c) => ultima.get(c.questionario_id)?.id !== c.id);

    return (
        <div className="qz-wrap">
            {errore && <p className="qz-errore"><AlertTriangle size={15} /> {errore}</p>}
            {messaggio && <p className="qz-messaggio">{messaggio}</p>}

            {!questionari.length ? (
                <div className="qz-vuoto">
                    <FileText size={32} strokeWidth={1.4} />
                    <p><strong>Nessuna scheda di questo tipo caricata su questa installazione.</strong></p>
                    <p className="qz-vuoto-nota">
                        Gli strumenti non vengono distribuiti col software: si caricano con
                        <code> node backend/scripts/caricaQuestionario.js questionari/</code>
                    </p>
                </div>
            ) : (
                <div className="rm-scorre">
                <table className="qz-elenco">
                    <thead>
                        <tr>
                            <th>{etichetta === 'scheda' ? 'Scheda' : 'Questionario'}</th>
                            <th className="num">{etichetta === 'scheda' ? 'Campi' : 'Domande'}</th>
                            <th>Stato</th>
                            <th />
                        </tr>
                    </thead>
                    <tbody>
                        {questionari.map((q) => {
                            const c = ultima.get(q.id);
                            const s = c ? STATO[c.stato] : null;
                            return (
                                <tr key={q.id}>
                                    <td>
                                        <b>{q.sigla || q.nome}</b>
                                        {q.sigla && <span className="qz-sub">{q.nome}</span>}
                                    </td>
                                    <td className="num">{q.numero_item}</td>
                                    <td>
                                        {c ? (
                                            <span className={`qz-stato ${s.classe}`}>
                                                <s.Icona size={13} />
                                                {/* Una scheda a quattro mani in attesa non
                                                    e' "in attesa sul tablet": il paziente ha
                                                    finito, tocca al medico. Scriverlo come
                                                    prima manderebbe la segreteria a cercare
                                                    un tablet che ha gia' fatto il suo. */}
                                                {c.stato === 'in_attesa' && c.risposte_date > 0
                                                    ? 'risposte del paziente arrivate — da completare'
                                                    : s.testo}
                                                <span className="qz-quando">
                                                    {c.stato === 'compilato'
                                                        ? dataOra(c.compilata_il)
                                                        : dataOra(c.avviata_il)}
                                                    {(c.stato === 'compilato'
                                                      || c.risposte_date > 0)
                                                        && ` · ${c.risposte_date}/${q.numero_item}`}
                                                </span>
                                            </span>
                                        ) : <span className="qz-mai">mai somministrato</span>}
                                    </td>
                                    <td className="qz-azioni-cella">
                                        {c && c.stato === 'compilato' && (
                                            <button className="qz-btn" onClick={() => setApri(c.id)}>
                                                <Eye size={14} /> Leggi
                                            </button>
                                        )}
                                        {c && c.stato === 'in_attesa' && (
                                            <button className="qz-btn qz-btn--pericolo"
                                                    onClick={() => annulla(c.id)} disabled={inCorso}>
                                                <X size={14} /> Annulla
                                            </button>
                                        )}
                                        {/* Una scheda che dalla sezione 12 in poi
                                            e' lavoro del medico non si manda al
                                            paziente: lo dice la definizione, non
                                            un elenco di eccezioni nel codice. */}
                                        {q.al_paziente !== false && (
                                            <button className="qz-btn"
                                                    onClick={() => { setScelta(q); setMessaggio(null); }}
                                                    disabled={inCorso}
                                                    title="Il paziente risponde da solo sul tablet">
                                                <TabletIcona size={14} />
                                                {c && c.stato === 'in_attesa' ? 'Rimanda' : 'Tablet'}
                                            </button>
                                        )}
                                        <button className="qz-btn qz-btn--principale"
                                                onClick={() => compilaQui(q)}
                                                disabled={inCorso}
                                                title="Si compila qui, insieme al paziente">
                                            <PencilLine size={14} /> Compila qui
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                </div>
            )}

            {scelta && (
                <div className="qz-invio">
                    <p>
                        <b>{scelta.sigla || scelta.nome}</b> — {scelta.numero_item} domande.
                        Su quale postazione?
                    </p>
                    {dispositivi.length ? (
                        <div className="qz-azioni">
                            <select value={dispositivo} onChange={(e) => setDispositivo(e.target.value)}>
                                <option value="">Scegli la postazione…</option>
                                {dispositivi.map((d) => (
                                    <option key={d.id} value={d.id}>{d.nome}</option>
                                ))}
                            </select>
                            <button className="qz-btn" onClick={() => setScelta(null)}>Annulla</button>
                            <button className="qz-btn qz-btn--principale"
                                    onClick={invia} disabled={inCorso || !dispositivo}>
                                <Send size={14} /> Invia
                            </button>
                        </div>
                    ) : (
                        <p className="qz-vuoto-nota">
                            Nessuna postazione registrata: si aggiunge dalle impostazioni dei tablet.
                        </p>
                    )}
                    {/* Chi invia deve sapere che sul tablet compare al posto di
                        quello che c'e' adesso, non in coda. */}
                    <p className="qz-vuoto-nota">
                        Sostituisce quello che il tablet sta mostrando in questo momento.
                        L&apos;anagrafica del paziente la prende dalla scheda: non gli verrà chiesta.
                    </p>
                </div>
            )}

            {storico.length > 0 && (
                <details className="qz-storico">
                    <summary>Somministrazioni precedenti ({storico.length})</summary>
                    {/* Tabella larga: scorre nel suo riquadro, non esce dalla scheda. */}
                    <div className="rm-scorre">
                    <table className="qz-elenco">
                        <tbody>
                            {storico.map((c) => {
                                const s = STATO[c.stato];
                                return (
                                    <tr key={c.id}>
                                        <td><b>{c.sigla || c.nome}</b></td>
                                        <td>
                                            <span className={`qz-stato ${s.classe}`}>
                                                <s.Icona size={13} /> {s.testo}
                                            </span>
                                        </td>
                                        <td>{dataOra(c.compilata_il || c.annullata_il
                                                     || c.scaduta_il || c.avviata_il)}</td>
                                        <td className="qz-azioni-cella">
                                            {c.stato === 'compilato' && (
                                                <button className="qz-btn" onClick={() => setApri(c.id)}>
                                                    <Eye size={14} /> Leggi
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    </div>
                </details>
            )}
        </div>
    );
};

export default ElencoQuestionari;
